'use strict';

/**
 * Pict-Provider-TeamAPI
 * ---------------------
 * Browser-side client for team administration (the /1.0/Team/* endpoints):
 * members + their roles, custom roles, and the entitlement catalog. Backs the
 * Team and Roles sections of the settings hub. Cookie-authed (same-origin); the
 * server gates each route by entitlement.
 */

const libPictProvider = require('pict').ServiceProviderBase;
const libFrameworkClient = require('./FrameworkClient.js');

const _DefaultProviderConfiguration =
{
	ProviderIdentifier: 'TeamAPI',
	Dependencies: [],
	RoutePrefix: '/1.0/'
};

class PictProviderTeamAPI extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		let tmpOptions = Object.assign({}, _DefaultProviderConfiguration, pOptions || {});
		super(pFable, tmpOptions, pServiceHash);
		this.routePrefix = this.options.RoutePrefix;
	}

	initializeAppData()
	{
		if (!this.fable.AppData.Team) { this.fable.AppData.Team = { Members: [], Roles: [], Catalog: [], Invitations: [], KnownEmails: [] }; }
	}

	_url(pPath)
	{
		let tmpPrefix = this.routePrefix;
		if (tmpPrefix.charAt(tmpPrefix.length - 1) !== '/') { tmpPrefix += '/'; }
		return tmpPrefix + pPath.replace(/^\//, '');
	}

	_get(pPath)
	{
		return libFrameworkClient.getJSON(this.fable, this._url(pPath))
			.then((pBody) => ({ ok: true, status: 200, body: pBody }))
			.catch((pError) => ({ ok: false, status: pError.StatusCode || 0, body: {} }));
	}

	_send(pMethod, pPath, pBody)
	{
		let tmpVerb = (pMethod === 'PUT') ? libFrameworkClient.putJSON : (pMethod === 'DELETE') ? libFrameworkClient.delJSON : libFrameworkClient.postJSON;
		let tmpArgs = (pMethod === 'DELETE') ? [this.fable, this._url(pPath)] : [this.fable, this._url(pPath), pBody || {}];
		return tmpVerb.apply(libFrameworkClient, tmpArgs)
			.then((pParsed) =>
			{
				if (pParsed && pParsed.Success === false) { throw new Error((pParsed && pParsed.Error) || 'Request failed.'); }
				return pParsed;
			});
	}

	listMembers()
	{
		this.initializeAppData();
		return this._get('Team/Members').then((pResult) =>
		{
			this.fable.AppData.Team.Members = (pResult.ok && pResult.body.Members) ? pResult.body.Members : [];
			return this.fable.AppData.Team.Members;
		});
	}

	// One teammate's public profile (signed-in; no admin entitlement). Used by /profile/:id.
	getMember(pIDUser)
	{
		return this._get('Team/Members/' + pIDUser).then((pResult) => (pResult.ok && pResult.body && pResult.body.Member) ? pResult.body.Member : null);
	}

	// The public team roster (signed-in; public profile fields). Powers the community hub.
	roster()
	{
		this.initializeAppData();
		return this._get('Team/Roster').then((pResult) =>
		{
			this.fable.AppData.Team.Roster = (pResult.ok && pResult.body && pResult.body.Roster) ? pResult.body.Roster : [];
			return this.fable.AppData.Team.Roster;
		});
	}

	listRoles()
	{
		this.initializeAppData();
		return this._get('Team/Roles').then((pResult) =>
		{
			this.fable.AppData.Team.Roles = (pResult.ok && pResult.body.Roles) ? pResult.body.Roles : [];
			return this.fable.AppData.Team.Roles;
		});
	}

	getEntitlements()
	{
		this.initializeAppData();
		return this._get('Team/Entitlements').then((pResult) =>
		{
			this.fable.AppData.Team.Catalog = (pResult.ok && pResult.body.Entitlements) ? pResult.body.Entitlements : [];
			return this.fable.AppData.Team.Catalog;
		});
	}

	updateMember(pIDUser, pPatch) { return this._send('PUT', 'Team/Members/' + pIDUser, pPatch || {}); }
	// Deactivate a persona (the "remove from this plan sheet" action). Keeps their attribution on past
	// work; a later invite reactivates them. The server guards the last admin and self-removal.
	removeMember(pIDUser) { return this._send('PUT', 'Team/Members/' + pIDUser, { Active: 0 }); }

	// Invitations for the current plan sheet (the /1.0/Org/Invitations surface, admin-gated server-side).
	listInvitations()
	{
		this.initializeAppData();
		return this._get('Org/Invitations').then((pResult) =>
		{
			this.fable.AppData.Team.Invitations = (pResult.ok && pResult.body.Invitations) ? pResult.body.Invitations : [];
			return this.fable.AppData.Team.Invitations;
		});
	}

	// Email addresses the account already works with, across every plan sheet it belongs to -- for the
	// invite autocomplete. Best-effort: an empty list just means no suggestions.
	knownEmails()
	{
		this.initializeAppData();
		return this._get('Account/KnownMemberEmails').then((pResult) =>
		{
			this.fable.AppData.Team.KnownEmails = (pResult.ok && pResult.body.Emails) ? pResult.body.Emails : [];
			return this.fable.AppData.Team.KnownEmails;
		});
	}

	invite(pEmail, pRole) { return this._send('POST', 'Org/Invitations', { Email: pEmail, Role: pRole || 'member' }); }
	resendInvitation(pIDInvitation) { return this._send('POST', 'Org/Invitations/' + pIDInvitation + '/Resend', {}); }
	revokeInvitation(pIDInvitation) { return this._send('DELETE', 'Org/Invitations/' + pIDInvitation); }
	resetMemberTwoFactor(pIDUser) { return this._send('POST', 'Team/Members/' + pIDUser + '/ResetTwoFactor', {}); }
	// Retire an agent persona (WI #465): soft-deletes it once it has no active node (the server 409s otherwise).
	retireAgent(pIDUser) { return this._send('POST', 'Node/Agent/' + pIDUser + '/Retire', {}); }
	createRole(pSpec) { return this._send('POST', 'Team/Roles', pSpec || {}); }
	updateRole(pIDRole, pSpec) { return this._send('PUT', 'Team/Roles/' + pIDRole, pSpec || {}); }
	deleteRole(pIDRole) { return this._send('DELETE', 'Team/Roles/' + pIDRole); }
}

module.exports = PictProviderTeamAPI;
module.exports.default_configuration = _DefaultProviderConfiguration;
